import { createFileRoute } from '@tanstack/react-router';
import React, { useEffect } from 'react';
import { trpc } from '../../trpc';
import { toast } from 'sonner';
import { analytics } from '../../lib/analytics';
import {
  ResumeEvaluationLoading,
  ResumeEvaluationError,
  MissingApplicationError,
} from '../../components/evaluation';

// This is the main entry point for resume evaluations
export const Route = createFileRoute('/evaluation/')({
  component: EvaluationPage,
});

function EvaluationPage() {
  // Get the applicationId from the URL query parameters
  const { applicationId: appIdParam } = Route.useSearch() as {
    applicationId?: string;
  };
  const applicationId = appIdParam ? parseInt(appIdParam, 10) : undefined;

  console.log('*** Evaluation Page - Application ID:', applicationId);

  // Use tRPC utils for cache invalidation
  const utils = trpc.useUtils();

  // Mutation for creating an evaluation
  const evaluateMutation = trpc.resumeEvaluation.evaluateResume.useMutation({
    onSuccess: (data: any) => {
      console.log('Evaluation successful:', data);
      analytics.track('resume_match_score_generated');

      // Invalidate relevant queries to refresh data everywhere
      utils.resumeEvaluation.getLatestEvaluation.invalidate();
      utils.resumeEvaluation.getUsageStats.invalidate();
      utils.resumeEvaluation.getById.invalidate();
      utils.applications.getAll.invalidate();

      // Force refetch in the background for immediate updates
      if (applicationId) {
        utils.resumeEvaluation.getLatestEvaluation.refetch({ applicationId });
      }
      utils.resumeEvaluation.getUsageStats.refetch();

      // IMPORTANT: Rewrite the URL to include the evaluation ID
      // This prevents accidental re-evaluations on page refresh
      if (data && data.id) {
        console.log(
          `Evaluation successful, redirecting to /evaluation/${data.id}`
        );

        // We don't use history.pushState to avoid triggering router changes
        // Instead, we use window.location.replace to perform a "silent" redirect
        // that replaces the current history entry
        window.location.replace(`/evaluation/${data.id}`);
      } else {
        console.error('Evaluation completed but no ID was returned');
        toast.error('Evaluation completed but no result was returned');
      }
    },
    onError: (error: any) => {
      console.error('Evaluation failed:', error);
      toast.error(error.message || 'Failed to evaluate resume');
    },
  });

  // This flag helps us avoid double-evaluations
  const hasStartedEvaluation = React.useRef(false);

  // Start evaluation when the component mounts if we have an applicationId
  useEffect(() => {
    // Only evaluate if we haven't already started an evaluation
    if (
      applicationId &&
      !isNaN(applicationId) &&
      !evaluateMutation.isLoading &&
      !evaluateMutation.isSuccess &&
      !hasStartedEvaluation.current
    ) {
      console.log('Starting evaluation for application:', applicationId);

      // Check if Clerk is loaded and has a session before proceeding
      // This is important for new windows where Clerk may not be initialized yet
      if (typeof window !== 'undefined' && window.Clerk && window.Clerk.session) {
        // Set the flag to prevent double evaluation
        hasStartedEvaluation.current = true;

        // Call the API to evaluate the resume against this application - THIS IS THE ONLY PLACE
        // THAT SHOULD CALL THIS ENDPOINT
        evaluateMutation.mutate({ applicationId });
      } else {
        // If Clerk isn't ready yet, retry after a short delay
        const retryTimeout = setTimeout(() => {
          if (
            applicationId &&
            !isNaN(applicationId) &&
            !evaluateMutation.isLoading &&
            !evaluateMutation.isSuccess &&
            !hasStartedEvaluation.current &&
            typeof window !== 'undefined' &&
            window.Clerk &&
            window.Clerk.session
          ) {
            console.log('Clerk loaded, starting evaluation for application:', applicationId);
            hasStartedEvaluation.current = true;
            evaluateMutation.mutate({ applicationId });
          }
        }, 500);

        return () => clearTimeout(retryTimeout);
      }
    }
  }, [applicationId, evaluateMutation]);

  // If no applicationId is provided, show an error
  if (!applicationId || isNaN(applicationId)) {
    return <MissingApplicationError />;
  }

  // Handle error display
  if (evaluateMutation.isError) {
    return (
      <ResumeEvaluationError error={evaluateMutation.error.message || ''} />
    );
  }

  // Show loading state
  return <ResumeEvaluationLoading />;
}
